import { useState, useEffect } from 'react';
import { ArrowLeft, Plane, ExternalLink, Calendar, Clock, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AnreiseFlugPage = () => {
  const navigate = useNavigate();
  const [direction, setDirection] = useState('nach');
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sourceNote, setSourceNote] = useState('');

  const providers = [
    {
      id: 'flughafen-sylt',
      name: 'Flughafen Sylt (GWT)',
      color: '#0E4C92',
      url: 'https://www.flughafen-sylt.de/fluggaeste/flugplan',
    },
    {
      id: 'skyscanner',
      name: 'Skyscanner',
      color: '#00A698',
      url: 'https://www.skyscanner.de/fluge/ankunft-abflug/gwt/westerland-sylt-ankunft-abflug',
    }
  ];

  // Daten laden
  useEffect(() => {
    const fetchSchedule = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/anreise/flug`, {
          params: { direction, date: selectedDate }
        });
        setSchedule(response.data.data || []);
        setSourceNote(response.data.source_note || '');
      } catch (err) {
        console.error('Fehler beim Laden:', err);
        setError('Flugplan konnte nicht geladen werden');
        setSchedule([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [direction, selectedDate]);

  // v2.8.0: Immer kompletten Tagesfahrplan anzeigen (kein Zeitfilter)
  const filteredSchedule = schedule;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0E4C92] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Chat
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Plane className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Flüge nach / von Sylt</h1>
          </div>
          <p className="text-white/80">Abflüge & Ankünfte am Flughafen Westerland (GWT)</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Hinweis */}
        <p className="text-sm text-gray-500 mb-6">
          Alle Angaben ohne Gewähr. Maßgeblich sind die Angaben der Anbieter.
        </p>

        {/* Filter & Anbieter in einem Block */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Linke Seite: Filter */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-4">Flugverbindung suchen</h2>
              
              {/* Richtung */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Richtung</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="nach"
                      checked={direction === 'nach'}
                      onChange={(e) => setDirection(e.target.value)}
                      className="w-4 h-4 text-[#0E4C92]"
                    />
                    <span>Nach Sylt (Ankünfte)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="von"
                      checked={direction === 'von'}
                      onChange={(e) => setDirection(e.target.value)}
                      className="w-4 h-4 text-[#0E4C92]"
                    />
                    <span>Von Sylt (Abflüge)</span>
                  </label>
                </div>
              </div>

              {/* Datum */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Datum
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E4C92] focus:border-transparent"
                />
              </div>
            </div>

            {/* Rechte Seite: Anbieter-Links */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-4">Flugpläne</h2>
              <div className="space-y-2">
                {providers.map((provider) => (
                  <a
                    key={provider.id}
                    href={provider.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                    style={{ borderLeftWidth: '3px', borderLeftColor: provider.color }}
                  >
                    <Plane className="w-5 h-5" style={{ color: provider.color }} />
                    <span className="flex-1 text-sm font-medium" style={{ color: provider.color }}>
                      {provider.name}
                    </span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* HAUPTBEREICH: Flugzeiten-Liste */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Flugzeiten
              </h2>
              <p className="text-sm text-gray-500">
                für {formatDate(selectedDate)} – {direction === 'nach' ? 'Ankünfte' : 'Abflüge'}
              </p>
            </div>
            {sourceNote && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                {sourceNote}
              </span>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0E4C92]" />
              <span className="ml-3 text-gray-600">Lade Flugplan...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-red-700">{error}</p>
              <p className="text-sm text-red-600 mt-2">
                Bitte prüfen Sie die Zeiten direkt beim Flughafen.
              </p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredSchedule.length === 0 && (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-600">
                Für den gewählten Zeitraum sind keine weiteren Flüge verfügbar.
              </p>
            </div>
          )}

          {/* Schedule List */}
          {!loading && !error && filteredSchedule.length > 0 && (
            <div className="space-y-3">
              {filteredSchedule.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-4 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  style={{ borderLeftWidth: '4px', borderLeftColor: item.status_color || '#0E4C92' }}
                >
                  {/* Uhrzeit */}
                  <div className="w-20">
                    <span className="text-xl font-bold text-gray-900">{item.time}</span>
                    {item.delay && (
                      <span className="block text-sm font-medium text-amber-600">
                        {item.delay}
                      </span>
                    )}
                  </div>

                  {/* Icon & Fluginfo */}
                  <div className="flex items-center gap-3 flex-1">
                    <Plane className="w-5 h-5 text-[#0E4C92]" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {item.flight_number} · {item.airline}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <ArrowRight className="w-3 h-3" />
                        {item.direction_label} {item.city}
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  {item.status && (
                    <span className={`text-xs px-2 py-1 rounded ${
                      item.status === 'pünktlich' ? 'bg-green-100 text-green-700' :
                      item.status === 'verspätet' ? 'bg-amber-100 text-amber-700' :
                      item.status === 'gestrichen' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {item.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wetter-Hinweis */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-medium text-amber-900 mb-2">Wetterhinweis</h3>
          <p className="text-sm text-amber-800">
            Flugzeiten können sich wetterbedingt kurzfristig ändern. 
            Bitte informieren Sie sich vor Abflug über den aktuellen Status.
          </p>
        </div>

        {/* Info Box */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2">Gut zu wissen</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Flughafen Sylt (IATA: GWT) liegt in Westerland</li>
            <li>• Verbindungen u.a. nach Berlin, Düsseldorf, Frankfurt, Hamburg, München</li>
            <li>• Flughafentransfer bitte vorher organisieren</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AnreiseFlugPage;
